import { useMemo } from 'react';
import type { CSSProperties, JSX } from 'react';
import type { Palette } from '../../../_theme/palette.js';
import { hexWithAlpha } from '../../../_theme/palette.js';
import { Stagger } from '../../../motion/Stagger.js';
import { decodeAnnouncementBody } from '../utils/decodeYjsBody.js';
import { collectText } from '../tiptap/types.js';
import { formatPublishedDate, formatRelativeDate, type AnnouncementShape } from '../types.js';

interface FeedViewProps {
  palette: Palette;
  announcements: AnnouncementShape[];
  loading: boolean;
  activeCollectionSlug: string | null;
  showFilters: boolean;
  onSelectCollection: (slug: string | null) => void;
  onSelectAnnouncement: (announcement: AnnouncementShape) => void;
  onClose?: (() => void) | null;
  title?: string;
  description?: string;
}

export function FeedView({
  palette,
  announcements,
  loading,
  activeCollectionSlug,
  showFilters,
  onSelectCollection,
  onSelectAnnouncement,
  onClose,
  title,
  description,
}: FeedViewProps): JSX.Element {
  const collections = useMemo(
    () => (showFilters ? collectCollections(announcements) : []),
    [announcements, showFilters],
  );
  const groups = useMemo(() => groupByDay(announcements), [announcements]);

  return (
    <div style={wrapperStyle(palette)}>
      {(title || description || onClose) && (
        <header style={headerStyle(palette)}>
          <div style={{ minWidth: 0 }}>
            {title && <h1 style={titleStyle(palette)}>{title}</h1>}
            {description && <p style={descriptionStyle(palette)}>{description}</p>}
          </div>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              style={closeButtonStyle(palette)}
              onMouseEnter={(event) => (event.currentTarget.style.background = hexWithAlpha(palette.text, 0.06))}
              onMouseLeave={(event) => (event.currentTarget.style.background = 'transparent')}
            >
              ×
            </button>
          )}
        </header>
      )}

      {collections.length > 0 && (
        <div style={filterRowStyle(palette)}>
          <FilterPill
            palette={palette}
            active={activeCollectionSlug === null}
            label="All"
            onClick={() => onSelectCollection(null)}
          />
          {collections.map((collection) => (
            <FilterPill
              key={collection.slug}
              palette={palette}
              active={activeCollectionSlug === collection.slug}
              label={collection.name}
              onClick={() => onSelectCollection(collection.slug)}
            />
          ))}
        </div>
      )}

      <div style={feedStyle()}>
        {loading ? (
          <FeedSkeleton palette={palette} />
        ) : announcements.length === 0 ? (
          <p style={emptyStyle(palette)}>No announcements yet.</p>
        ) : (
          <Timeline palette={palette} groups={groups} onSelect={onSelectAnnouncement} />
        )}
      </div>
    </div>
  );
}

interface GroupedDay {
  key: string;
  relative: string | null;
  absolute: string | null;
  announcements: AnnouncementShape[];
}

function Timeline({
  palette,
  groups,
  onSelect,
}: {
  palette: Palette;
  groups: GroupedDay[];
  onSelect: (announcement: AnnouncementShape) => void;
}): JSX.Element {
  return (
    <div style={timelineWrapStyle(palette)}>
      {groups.map((group) => (
        <section key={group.key} style={groupRowStyle()}>
          {/* Timeline column — right-aligned, dot + relative-time on row 1, date on row 2 */}
          <div style={dateColumnStyle()}>
            {group.relative && (
              <div style={dateTopRowStyle()}>
                <span style={timelineDotStyle(palette)} />
                <span style={relativeStyle(palette)}>{group.relative}</span>
              </div>
            )}
            {group.absolute && <div style={absoluteStyle(palette)}>{group.absolute}</div>}
          </div>

          {/* Entries column — vertical line is the border-left here */}
          <div style={entriesColumnStyle(palette)}>
            <Stagger>
              {group.announcements.map((announcement) => (
                <TimelineEntry
                  key={announcement._id ?? announcement.publicId ?? ''}
                  palette={palette}
                  announcement={announcement}
                  onClick={() => onSelect(announcement)}
                />
              ))}
            </Stagger>
          </div>
        </section>
      ))}
    </div>
  );
}

interface TimelineEntryProps {
  palette: Palette;
  announcement: AnnouncementShape;
  onClick: () => void;
}

function TimelineEntry({ palette, announcement, onClick }: TimelineEntryProps): JSX.Element {
  const preview = useMemo(() => {
    if (announcement.description) return announcement.description;
    if (announcement.bodySummary) return announcement.bodySummary;
    const decoded = decodeAnnouncementBody(announcement.body);
    return decoded?.content ? truncate(collectText(decoded.content), 220) : '';
  }, [announcement.description, announcement.bodySummary, announcement.body]);

  return (
    <button type="button" onClick={onClick} style={entryButtonStyle(palette)}>
      {announcement.badge && (
        <span style={badgeStyle(palette, announcement.badgeColor)}>{announcement.badge}</span>
      )}
      <h3 style={entryTitleStyle(palette)}>{announcement.title ?? 'Announcement'}</h3>
      {preview && <p style={entryPreviewStyle(palette)}>{preview}</p>}
    </button>
  );
}

function FilterPill({
  palette,
  active,
  label,
  onClick,
}: {
  palette: Palette;
  active: boolean;
  label: string;
  onClick: () => void;
}): JSX.Element {
  return (
    <button type="button" onClick={onClick} style={filterPillStyle(palette, active)}>
      {label}
    </button>
  );
}

function FeedSkeleton({ palette }: { palette: Palette }): JSX.Element {
  const bar = hexWithAlpha(palette.text, 0.06);
  return (
    <div style={timelineWrapStyle(palette)}>
      {[0, 1].map((index) => (
        <section key={index} style={groupRowStyle()}>
          <div style={dateColumnStyle()}>
            <div style={dateTopRowStyle()}>
              <span style={timelineDotStyle(palette)} />
              <span style={{ ...skeletonLineStyle(bar), width: 60, height: 10 }} />
            </div>
            <span style={{ ...skeletonLineStyle(bar), width: 80, height: 12, marginTop: 4 }} />
          </div>
          <div style={entriesColumnStyle(palette)}>
            <div style={{ ...skeletonLineStyle(bar), width: '60%', height: 20 }} />
            <div style={{ ...skeletonLineStyle(bar), width: '92%', height: 12, marginTop: 10 }} />
            <div style={{ ...skeletonLineStyle(bar), width: '78%', height: 12, marginTop: 6 }} />
          </div>
        </section>
      ))}
    </div>
  );
}

function groupByDay(announcements: AnnouncementShape[]): GroupedDay[] {
  const buckets = new Map<string, GroupedDay>();
  for (const announcement of announcements) {
    const key = announcement.publishedAt ? localDayKey(announcement.publishedAt) : 'unknown';
    let group = buckets.get(key);
    if (!group) {
      group = {
        key,
        relative: formatRelativeDate(announcement.publishedAt),
        absolute: formatPublishedDate(announcement.publishedAt),
        announcements: [],
      };
      buckets.set(key, group);
    }
    group.announcements.push(announcement);
  }
  return Array.from(buckets.values());
}

// Local-tz bucketing — matches the main app's portal so late-evening items don't jump a day.
function localDayKey(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'unknown';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function collectCollections(announcements: AnnouncementShape[]): Array<{ slug: string; name: string }> {
  const map = new Map<string, string>();
  for (const announcement of announcements) {
    const slug = announcement.collectionSlug;
    const name = announcement.collectionName;
    if (slug && name && !map.has(slug)) map.set(slug, name);
  }
  return Array.from(map.entries()).map(([slug, name]) => ({ slug, name }));
}

function truncate(value: string, max: number): string {
  const trimmed = value.trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max - 1).trim()}…`;
}

/* ─── Styles ────────────────────────────────────────────────────────────── */

function wrapperStyle(palette: Palette): CSSProperties {
  return {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    background: palette.background,
    color: palette.text,
    fontFamily: palette.font,
  };
}

function headerStyle(palette: Palette): CSSProperties {
  return {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: '28px 32px 20px',
    borderBottom: `1px solid ${palette.border}`,
    background: palette.background,
    flexShrink: 0,
  };
}

function titleStyle(palette: Palette): CSSProperties {
  return {
    margin: 0,
    fontSize: 24,
    fontWeight: 700,
    color: palette.text,
    letterSpacing: -0.3,
    fontFamily: palette.font,
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
  };
}

function descriptionStyle(palette: Palette): CSSProperties {
  return {
    margin: '6px 0 0',
    fontSize: 14,
    color: palette.textMuted,
    lineHeight: 1.55,
    fontFamily: palette.font,
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
  };
}

function closeButtonStyle(palette: Palette): CSSProperties {
  return {
    width: 32,
    height: 32,
    borderRadius: 8,
    border: 'none',
    background: 'transparent',
    color: palette.textMuted,
    fontSize: 20,
    lineHeight: 1,
    cursor: 'pointer',
    transition: 'background 120ms ease',
    flexShrink: 0,
  };
}

function filterRowStyle(palette: Palette): CSSProperties {
  return {
    display: 'flex',
    gap: 8,
    padding: '14px 32px',
    flexWrap: 'wrap',
    borderBottom: `1px solid ${palette.border}`,
    background: palette.background,
    flexShrink: 0,
  };
}

function filterPillStyle(palette: Palette, active: boolean): CSSProperties {
  // Matches portal: `px-3 py-1 text-xs rounded-full` with primary bg when active.
  return {
    padding: '4px 12px',
    borderRadius: 999,
    border: 'none',
    background: active ? palette.primary : hexWithAlpha(palette.text, 0.05),
    color: active ? palette.onPrimary : palette.textMuted,
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 120ms ease',
    fontFamily: palette.font,
  };
}

function feedStyle(): CSSProperties {
  return { flex: 1, overflowY: 'auto', padding: '28px 40px 40px', minHeight: 0 };
}

function timelineWrapStyle(palette: Palette): CSSProperties {
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: 40,
    fontFamily: palette.font,
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
  };
}

function groupRowStyle(): CSSProperties {
  return { display: 'flex', gap: 32, alignItems: 'flex-start' };
}

function dateColumnStyle(): CSSProperties {
  return {
    width: 120,
    flexShrink: 0,
    textAlign: 'right',
    paddingTop: 4,
  };
}

function dateTopRowStyle(): CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 6,
  };
}

function timelineDotStyle(palette: Palette): CSSProperties {
  return {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: palette.primary,
    flexShrink: 0,
  };
}

function relativeStyle(palette: Palette): CSSProperties {
  return {
    fontSize: 12,
    color: palette.textMuted,
    fontWeight: 500,
    fontFamily: palette.font,
  };
}

function absoluteStyle(palette: Palette): CSSProperties {
  return {
    fontSize: 14,
    color: palette.text,
    fontWeight: 600,
    marginTop: 4,
    fontFamily: palette.font,
  };
}

// Vertical line uses border-left on the entries column, matching the portal's ChangelogFeed.tsx.
function entriesColumnStyle(palette: Palette): CSSProperties {
  return {
    flex: 1,
    minWidth: 0,
    borderLeft: `1px solid ${hexWithAlpha(palette.text, 0.08)}`,
    paddingLeft: 32,
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
  };
}

function entryButtonStyle(palette: Palette): CSSProperties {
  return {
    display: 'block',
    textAlign: 'left',
    padding: 0,
    border: 'none',
    background: 'transparent',
    color: palette.text,
    cursor: 'pointer',
    fontFamily: palette.font,
    width: '100%',
  };
}

function badgeStyle(palette: Palette, color: string | undefined): CSSProperties {
  // Matches main-app portal badge: 12% alpha bg over full-color text.
  const base = color ?? palette.primary;
  return {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 4,
    background: hexWithAlpha(base, 0.2),
    color: base,
    fontSize: 12,
    fontWeight: 600,
    marginBottom: 8,
  };
}

function entryTitleStyle(palette: Palette): CSSProperties {
  return {
    margin: 0,
    fontSize: 18,
    fontWeight: 600,
    color: palette.text,
    lineHeight: 1.35,
    fontFamily: palette.font,
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
  };
}

function entryPreviewStyle(palette: Palette): CSSProperties {
  return {
    margin: '4px 0 0',
    fontSize: 14,
    color: palette.textMuted,
    lineHeight: 1.55,
    fontFamily: palette.font,
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
  };
}

function emptyStyle(palette: Palette): CSSProperties {
  return { margin: '60px 0', color: palette.textMuted, textAlign: 'center', fontSize: 14 };
}

function skeletonLineStyle(color: string): CSSProperties {
  return {
    display: 'block',
    background: color,
    borderRadius: 6,
    animation: 'chirp-skeleton 1.2s ease-in-out infinite',
  };
}
